'use strict';

import i18next from 'i18next';
import moment from 'moment-timezone';
import { getUrlParams } from '../../utils/url.js';
import { getColour } from '../../shared/components/chart/chart-helpers.js';
import { Utils } from '../../utils/utils.js';
import {
  calculateHoursPerDay,
  renderBalanceErniGraph,
} from '../balance/balance.component.js';

const ERNI_SNP_COLOURS = ['#046BBC', '#DC878A', '#9C9C9C'];
const ERNI_SNP_LINE_COLOUR = '#9C9C9C';

const MONTH_NAMES = {
  es: [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sept',
    'Oct',
    'Nov',
    'Dic',
  ],
  en: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sept',
    'Oct',
    'Nov',
    'Dec',
  ],
};

const getErniValueMonthKey = (value) => {
  if (/^\d{4}-\d{2}$/.test(value?.monthKey ?? '')) return value.monthKey;

  const rawDate = value?.x ?? value?.datetime;
  if (rawDate == null || typeof rawDate === 'number') return null;

  const monthKey = moment(rawDate).tz('Europe/Madrid').format('YYYY-MM');
  return /^\d{4}-\d{2}$/.test(monthKey) ? monthKey : null;
};

const getErniMonthMoment = (value) => {
  const monthKey = getErniValueMonthKey(value);
  return monthKey
    ? moment.tz(`${monthKey}-01`, 'YYYY-MM-DD', 'Europe/Madrid')
    : null;
};

const generateMonthlyDates = (startDate, endDate, valuesList) => {
  const startMonth = getErniMonthMoment({ x: startDate });
  const endMonth = getErniMonthMoment({ x: endDate });
  if (!startMonth || !endMonth) return valuesList;

  const existingMonths = new Set(
    valuesList.map(getErniValueMonthKey).filter(Boolean)
  );
  const currentMonth = moment(startMonth);

  while (currentMonth.isSameOrBefore(endMonth, 'month')) {
    const monthKey = currentMonth.format('YYYY-MM');
    if (!existingMonths.has(monthKey)) {
      valuesList.push({ x: currentMonth.toDate(), monthKey, y: null });
    }
    currentMonth.add(1, 'month');
  }

  return valuesList;
};

const fillPrintVals = (rangeNumber, date) => {
  if (date == undefined) {
    date = moment().format('DD-MM-YYYY');
  }
  let arr = [];
  let dateSplit = date?.split('-');
  let utcTime = moment(date, 'DD-MM-YYYY').tz('Europe/Madrid').format();
  for (let i = 0; i < rangeNumber; i++) {
    arr.push({
      datetime:
        `${dateSplit[2]}-${dateSplit[1]}-${dateSplit[0]}T${
          i < 10 ? '0' + i : i
        }:00:00.000+` + utcTime.split('+')[1],
      datetime_utc: '2022-02-04T23:00:00Z',
      geo_id: null,
      geo_name: null,
      tz_time: '2022-02-04T23:00:00.000Z',
      value: 0,
    });
  }
  return arr;
};

const fillSimpleVals = () => {
  let arr = [];
  for (let i = 0; i < 14; i++) {
    arr.push({
      x: i,
      order: i,
      y: null,
      value: null,
      color: '#333333',
      name: '',
    });
  }
  return arr;
};

const getErniMonthLabels = (totalPoints, endDateMoment) => {
  const language = i18next.language === 'es' ? 'es' : 'en';
  const monthNames = MONTH_NAMES[language];
  const isPrint =
    new URLSearchParams(window.location.search).get('print') === 'true';

  // Fallback defensivo: si no llega un endDateMoment válido,
  // el gráfico no debe romper ni generar "undefined/NaN".
  const baseEnd = moment(endDateMoment);
  const endDate = baseEnd.isValid()
    ? baseEnd.tz('Europe/Madrid').startOf('month')
    : moment().tz('Europe/Madrid').startOf('month');

  return Array.from({ length: totalPoints }, (_, index) => {
    const monthDate = moment(endDate).subtract(
      totalPoints - index - 1,
      'months'
    );

    const monthIndex = monthDate.month();
    const safeMonthName =
      monthIndex >= 0 && monthIndex < monthNames.length
        ? monthNames[monthIndex]
        : '';

    return {
      key: index,
      label: isPrint ? safeMonthName : `${safeMonthName} ${monthDate.year()}`,
    };
  });
};

const renderErniSnpBarsLineGraph = (indicators, opts) => {
  const chartEndMoment = opts?.chartEndMoment;
  const container = document.querySelector(opts.elem);
  if (!container) {
    return false;
  }

  d3.select(container).select('svg').remove();

  const barIndicators = indicators.filter((indicator) => !indicator.demanda);
  const lineIndicator = indicators.find((indicator) => indicator.demanda);

  // Para SNP preferimos el dataset ORIGINAL (x=Date del mes) si existe,
  // evitando depender del buffer 0..13.
  const normalizeIndicatorForMonthMapping = (indicator) => {
    const src = Array.isArray(indicator?._sourceValues)
      ? indicator._sourceValues
      : null;
    if (!src || src.length === 0) return indicator;

    // Si el source trae x como Date/ISO y y/value, lo usamos para el mapeo por mes.
    return { ...indicator, values: src };
  };

  // Por defecto las series vienen con buffer fijo de 14 puntos.
  const defaultTotalPoints = Math.max(
    0,
    ...indicators.map((indicator) => indicator.values?.length || 0)
  );

  if (!defaultTotalPoints) {
    return false;
  }

  const resolvedTotalPoints =
    Number.isFinite(opts?.totalPoints) && opts.totalPoints > 0
      ? opts.totalPoints
      : defaultTotalPoints;

  // En este gráfico SNP, los valores llegan desde el component en `indicator.values[]`
  // con `datetime` (YYYY-MM-01T...) y `value` (y opcionalmente `y`).
  // Para evitar cualquier desalineación por offsets/buffers, mapeamos por mes contra los labels.
  const axisEndBase = moment(opts?.chartEndMoment || chartEndMoment);
  const axisEnd = axisEndBase.isValid()
    ? axisEndBase.tz('Europe/Madrid').startOf('month')
    : moment().tz('Europe/Madrid').startOf('month');

  // Labels SIEMPRE deben construirse con el axisEnd efectivo (ya recortado por mes vencido),
  // para que no aparezca JUL 2026 cuando no hay valores publicados.
  const labels = getErniMonthLabels(resolvedTotalPoints, axisEnd);

  const monthKeyForLabelIndex = (index) =>
    moment(axisEnd)
      .tz('Europe/Madrid')
      .startOf('month')
      .subtract(resolvedTotalPoints - index - 1, 'months')
      .format('YYYY-MM');

  const buildMonthValueMap = (indicator) => {
    // En ERNI SNP, `filteredIndicators` (desde component) trae valores mensuales con:
    //   { x: Date (mes real), y: number|null }  (y 0 es válido)
    // pero después en `renderErniWidget` se bufferiza a 14 posiciones y se pierde el Date.
    //
    // Para evitar corrimientos (JUN pintándose en MAY), aquí SOLO mapeamos por mes real
    // cuando `x` es Date válida; si no, usamos datetime; y si no hay fechas, fallback por índice.
    const values = Array.isArray(indicator?.values) ? indicator.values : [];
    const map = new Map();

    const isRealDateX =
      values.length > 0 &&
      values.every((v) => v?.x != null) &&
      values.some((v) => moment(v.x).isValid() && typeof v.x !== 'number') &&
      // cuando está bufferizado, x suele ser 0..13 (número)
      values.some((v) => typeof v.x !== 'number');

    const hasAnyDatetime = values.some(
      (v) => v?.datetime && moment(v.datetime).isValid()
    );

    values.forEach((v, idx) => {
      const val = v?.value ?? v?.y ?? null;
      if (val == null || Number.isNaN(val)) return;

      // Caso correcto para ERNI SNP: x es Date del mes
      const explicitMonthKey = getErniValueMonthKey(v);
      if (explicitMonthKey) {
        const key = explicitMonthKey;
        map.set(key, val);
        return;
      }

      // Alternativa: datetime presente
      if (hasAnyDatetime && v?.datetime && moment(v.datetime).isValid()) {
        const key = moment(v.datetime)
          .tz('Europe/Madrid')
          .startOf('month')
          .format('YYYY-MM');
        map.set(key, val);
        return;
      }

      // Fallback: indicador sin fechas (buffer puro)
      if (!isRealDateX && !hasAnyDatetime) {
        const monthsFromEnd = values.length - 1 - idx;
        const key = moment(axisEnd)
          .tz('Europe/Madrid')
          .startOf('month')
          .subtract(monthsFromEnd, 'months')
          .format('YYYY-MM');
        map.set(key, val);
      }
    });

    return map;
  };

  const rrttMap = buildMonthValueMap(
    normalizeIndicatorForMonthMapping(barIndicators[0])
  );
  const balanceMap = buildMonthValueMap(
    normalizeIndicatorForMonthMapping(barIndicators[1])
  );
  const totalMap = buildMonthValueMap(
    normalizeIndicatorForMonthMapping(lineIndicator)
  );

  const getIndexedValue = (monthMap, index) => {
    const key = monthKeyForLabelIndex(index);
    return monthMap.has(key) ? monthMap.get(key) : null;
  };

  const chartData = labels.map((labelItem, index) => ({
    ...labelItem,
    rrtt: getIndexedValue(rrttMap, index),
    balance: getIndexedValue(balanceMap, index),
    total: getIndexedValue(totalMap, index),
  }));

  const chartValues = chartData.flatMap((item) => [
    item.rrtt,
    item.balance,
    item.total,
  ]);
  const hasAnyValue = chartValues.some(
    (value) => value != null && !Number.isNaN(value)
  );
  if (!hasAnyValue) {
    return false;
  }
  const maxValue = chartValues.reduce((acc, value) => {
    if (value == null || Number.isNaN(value)) {
      return acc;
    }
    return Math.max(acc, value);
  }, 0);

  const parentWidth =
    container.parentElement?.getBoundingClientRect().width ||
    container.getBoundingClientRect().width ||
    1200;
  const margins = {
    top: 34,
    right: 24,
    bottom: parentWidth < 900 ? 108 : 84,
    left: 50,
  };
  const width = Math.max(parentWidth - margins.left - margins.right, 360);
  const innerHeight = width < 700 ? 290 : 335;
  const totalHeight = innerHeight + margins.top + margins.bottom;
  const svgWidth = width + margins.left + margins.right;

  const svg = d3
    .select(container)
    .append('svg')
    .attr('width', svgWidth)
    .attr('height', totalHeight)
    .attr('viewBox', `0 0 ${svgWidth} ${totalHeight}`)
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .style('width', '100%')
    .style('height', 'auto')
    .style('display', 'block')
    .style('margin', '0 auto');

  const chart = svg
    .append('g')
    .attr('transform', `translate(${margins.left},${margins.top})`);

  const yMax = Math.max(10, Math.ceil(maxValue));
  const x0 = d3.scale
    .ordinal()
    .domain(chartData.map((item) => item.key))
    .rangeRoundBands([0, width], 0.35);
  const y = d3.scale.linear().domain([0, yMax]).range([innerHeight, 0]);
  const barWidth = Math.max(
    18,
    Math.min(34, Math.round(x0.rangeBand() * 0.74))
  );

  const yAxis = d3.svg
    .axis()
    .scale(y)
    .orient('left')
    .ticks(10)
    .tickFormat((value) => value.toFixed(1))
    .tickSize(0);

  const xAxis = d3.svg
    .axis()
    .scale(x0)
    .orient('bottom')
    .tickFormat((key) => chartData[key]?.label || '');

  chart
    .selectAll('.erni-grid-line')
    .data(y.ticks(10))
    .enter()
    .append('line')
    .attr('class', 'erni-grid-line')
    .attr('x1', 0)
    .attr('x2', width)
    .attr('y1', (value) => y(value))
    .attr('y2', (value) => y(value))
    .attr('stroke', '#C9D2DB')
    .attr('stroke-dasharray', '8,6')
    .attr('shape-rendering', 'crispEdges');

  chart
    .append('g')
    .attr('class', 'erni-y-axis')
    .call(yAxis)
    .call((axis) => {
      axis.selectAll('path').style('display', 'none');
      axis.selectAll('line').style('display', 'none');
      axis
        .selectAll('text')
        .style('fill', '#5B6670')
        .style('font-size', '15px')
        .style('font-weight', '600');
    });

  const monthGroups = chart
    .selectAll('.erni-month-group')
    .data(chartData)
    .enter()
    .append('g')
    .attr('class', 'erni-month-group')
    .attr('transform', (item) => `translate(${x0(item.key)},0)`);

  monthGroups
    .append('rect')
    .attr('class', 'erni-bar erni-bar-rrtt')
    .attr('x', (x0.rangeBand() - barWidth) / 2)
    .attr('y', (item) => (item.rrtt == null ? innerHeight : y(item.rrtt)))
    .attr('width', barWidth)
    .attr('height', (item) =>
      item.rrtt == null ? 0 : Math.max(innerHeight - y(item.rrtt), 0)
    )
    .attr('fill', ERNI_SNP_COLOURS[0]);

  monthGroups
    .append('rect')
    .attr('class', 'erni-bar erni-bar-balance')
    .attr('x', (x0.rangeBand() - barWidth) / 2)
    .attr('y', (item) => {
      if (item.balance == null || item.balance === 0) {
        return innerHeight;
      }
      const stackedValue = (item.rrtt || 0) + item.balance;
      return y(stackedValue);
    })
    .attr('width', barWidth)
    .attr('height', (item) => {
      if (item.balance == null || item.balance === 0) {
        return 0;
      }
      return Math.max(
        y(item.rrtt || 0) - y((item.rrtt || 0) + item.balance),
        0
      );
    })
    .attr('fill', ERNI_SNP_COLOURS[1]);

  const line = d3.svg
    .line()
    .defined((item) => item.total != null)
    .x((item) => x0(item.key) + x0.rangeBand() / 2)
    .y((item) => y(item.total))
    .interpolate('linear');

  chart
    .append('path')
    .datum(chartData)
    .attr('class', 'erni-total-line')
    .attr('d', line)
    .attr('fill', 'none')
    .attr('stroke', ERNI_SNP_LINE_COLOUR)
    .attr('stroke-width', 1.2);

  chart
    .append('g')
    .attr('class', 'erni-x-axis')
    .attr('transform', `translate(0,${innerHeight})`)
    .call(xAxis)
    .call((axis) => {
      axis.selectAll('path').style('display', 'none');
      axis.selectAll('line').style('display', 'none');
      axis
        .selectAll('text')
        .style('fill', '#6B5C4B')
        .style('font-size', '13px')
        .style('font-weight', '700')
        .style('text-anchor', 'middle')
        .attr('dy', '1.1em');
    });

  chart
    .append('text')
    .attr('x', -18)
    .attr('y', -12)
    .style('fill', '#000000')
    .style('font-size', '15px')
    .style('font-weight', '600')
    .text('%');

  chart
    .append('text')
    .attr('x', -28)
    .attr('y', innerHeight + 22)
    .style('fill', '#000000')
    .style('font-size', '14px')
    .style('font-weight', '600')
    .text(i18next.t('months'));

  const legendItems = [
    {
      label:
        i18next.language === 'es'
          ? 'Porcentaje ERNI RRTT SNP'
          : 'Percentage ERNI RRTT SNP',
      color: ERNI_SNP_COLOURS[0],
      type: 'bar',
    },
    {
      label:
        i18next.language === 'es'
          ? 'Porcentaje ERNI Balance SNP'
          : 'Percentage ERNI Balance SNP',
      color: ERNI_SNP_COLOURS[1],
      type: 'bar',
    },
    {
      label:
        i18next.language === 'es'
          ? 'Porcentaje ERNI Total SNP'
          : 'Percentage ERNI Total SNP',
      color: ERNI_SNP_LINE_COLOUR,
      type: 'line',
    },
  ];
  const legend = chart
    .selectAll('.erni-legend-item')
    .data(legendItems)
    .enter()
    .append('g')
    .attr('class', 'erni-legend-item');

  legend.each(function (item) {
    const legendItem = d3.select(this);

    if (item.type === 'line') {
      legendItem
        .append('line')
        .attr('x1', 0)
        .attr('x2', 16)
        .attr('y1', 0)
        .attr('y2', 0)
        .attr('stroke', item.color)
        .attr('stroke-width', 3);
    } else {
      legendItem
        .append('rect')
        .attr('x', 0)
        .attr('y', -2)
        .attr('width', 12)
        .attr('height', 4)
        .attr('fill', item.color);
    }

    legendItem
      .append('text')
      .attr('x', 22)
      .attr('y', 4)
      .style('fill', '#000000')
      .style('font-size', '14px')
      .style('font-weight', '600')
      .text(item.label);
  });

  const legendNodes = legend[0] || [];
  const legendRows = width < 900 ? [[0, 1], [2]] : [[0, 1, 2]];
  const legendGap = width < 900 ? 28 : 40;
  const legendBaseY = innerHeight + 56;

  legendRows.forEach((row, rowIndex) => {
    const rowWidths = row.map(
      (index) => legendNodes[index]?.getBBox().width || 0
    );
    const totalRowWidth =
      rowWidths.reduce((sum, itemWidth) => sum + itemWidth, 0) +
      legendGap * Math.max(row.length - 1, 0);
    let currentX = Math.max((width - totalRowWidth) / 2, 0);
    const currentY = legendBaseY + rowIndex * 24;

    row.forEach((legendIndex, itemIndex) => {
      d3.select(legendNodes[legendIndex]).attr(
        'transform',
        `translate(${currentX},${currentY})`
      );
      currentX += rowWidths[itemIndex] + legendGap;
    });
  });

  return true;
};

const hasAnyNonNullValueForMonth = (indicators, monthMoment) => {
  const monthStr = moment(monthMoment).tz('Europe/Madrid').format('YYYY-MM');

  return (Array.isArray(indicators) ? indicators : []).some((indicator) => {
    const values = Array.isArray(indicator?.values) ? indicator.values : [];

    return values.some((v) => {
      // Regla de negocio confirmada:
      // - value=0 ES un dato válido (el mes existe y debe pintarse).
      // - Un mes “sin dato” es cuando NO viene en el array, o viene value/y null/undefined.
      const vMonthStr = getErniValueMonthKey(v);

      if (vMonthStr !== monthStr) return false;

      const yValRaw = v?.y ?? v?.value ?? null;
      return yValRaw != null;
    });
  });
};

const getLatestErniDataMonth = (indicators) => {
  let latestMonth = null;

  (Array.isArray(indicators) ? indicators : []).forEach((indicator) => {
    const values = Array.isArray(indicator?.values) ? indicator.values : [];
    values.forEach((value) => {
      if ((value?.y ?? value?.value ?? null) == null) return;
      const monthMoment = getErniMonthMoment(value);
      if (
        monthMoment?.isValid() &&
        (!latestMonth || monthMoment.isAfter(latestMonth, 'month'))
      ) {
        latestMonth = monthMoment;
      }
    });
  });

  return latestMonth;
};

const resolveErniAxisWindow = ({ date, totalPoints, indicators }) => {
  const requestedEnd = moment(date, 'DD-MM-YYYY')
    .tz('Europe/Madrid')
    .startOf('month');

  let endDate = moment(requestedEnd);
  const latestDataMonth = getLatestErniDataMonth(indicators);

  if (latestDataMonth && requestedEnd.isAfter(latestDataMonth, 'month')) {
    endDate = moment(latestDataMonth);
  }

  return { endDate, resolvedTotalPoints: totalPoints, sliceOffset: 0 };
};

const renderErniWidget = async (
  data,
  date,
  graphic,
  noDataGraphic,
  graphicalmuadilla,
  snp,
  datason,
  chartEndMoment
) => {
  //let { date } = getUrlParams();
  let isMobile = Utils.isMobileDevice();
  let ruta = window.location.pathname;
  let partes = ruta.split('/');
  let idioma = partes[1];
  let ernitotal = 'Porcentaje ERNI Total SNP';
  if (idioma == 'en') {
    ernitotal = 'ERNI Percentage of Total SNP';
  }

  const indicators = data.indicators;

  indicators.forEach((indicador) => {
    // Verificar si el id es 10460, 10461 o 10462 y el idioma es 'es'
    switch (indicador.id) {
      case 10488:
        if (idioma == 'es') {
          indicador.name = 'Porcentaje ERNI RRTT SNP';
        } else {
          indicador.name = 'Percentage ERNI RRTT SNP';
        }
        break;
      case 10489:
        if (idioma == 'es') {
          indicador.name = 'Porcentaje ERNI Balance SNP';
        } else {
          indicador.name = 'ERNI Percentage SNP Balance';
        }
        break;
      case 10490:
        if (idioma == 'es') {
          indicador.name = 'Porcentaje ERNI Total SNP';
        } else {
          indicador.name = 'ERNI Percentage Total SNP';
        }
        break;
      default:
        break;
    }
  });

  // Resolver ventana del eje X (endDate + puntos) según reglas de “mes vencido”.
  // OJO: no depende de chartEndMoment; se decide por presencia de datos (julio vs junio) en los indicadores.
  const axisWindow = resolveErniAxisWindow({
    date,
    totalPoints: 14,
    indicators,
  });

  let opts = {
    type: 'multi',
    special: true,
    fit: true,
    multibar: true,
    margin: { top: 20, right: 0, bottom: 40, left: 50 },
    lineInterpolation: 'linear',
    elem: graphicalmuadilla,
    height: isMobile ? 380 : 500,
    date: date,
    chartEndMoment: axisWindow.endDate,
    totalPoints: axisWindow.resolvedTotalPoints,
    sliceOffset: axisWindow.sliceOffset,
    yticks: 10,
    left: {
      label: '%',
    },
    bottom: {
      label: 'months',
      prefix: '0',
    },
    magnitud: '%',
  };

  // Sincronizar a primer día del mes
  date = date.replace(/^\\d{2}-/, '01-');

  let valh = calculateHoursPerDay(date);
  if (valh !== 25) {
    valh = 24;
  }

  let preData = {
    ...data,
    indicators: data.indicators.map((el) => {
      return {
        ...el,
        // Mantener copia del array original (con x=Date del mes) para el gráfico SNP,
        // porque el buffer de 14 posiciones pierde la referencia temporal y puede provocar corrimientos.
        _sourceValues: Array.isArray(el?.values) ? el.values : [],
        values: fillSimpleVals(valh),
      };
    }),
  };

  let dataOrdered = Utils.orderObjectsArrayByKey(
    preData.indicators,
    [10488, 10489, 10490],
    'id'
  );

  const fechaActual = new Date();
  const mes = String(fechaActual.getMonth() + 1).padStart(2, '0');
  const año = fechaActual.getFullYear();

  // Utilizar split para descomponer la fecha
  let dateParts = date.split('-');

  // Extraer el mes y el año
  let mesdate = dateParts[1];
  let añodate = dateParts[2];

  const dateObject = moment(date, 'DD-MM-YYYY').toDate(); //Última
  let iniDateObject = moment(dateObject).subtract(13, 'months').toDate(); // Primera
  if (mes == mesdate && año == añodate) {
    iniDateObject = moment(dateObject).subtract(14, 'months').toDate(); // Primera
  }

  dataOrdered.map((el, index) => {
    let finded =
      data.indicators.filter((or) => or.id === el.id)[0]?.values || [];

    let currentIndexValues = 0;
    if (finded.length > 0) {
      let primerFindedDate = moment(finded[0].x).toDate();
      if (iniDateObject < primerFindedDate) {
        let tempIniDateObject = iniDateObject;
        while (tempIniDateObject < primerFindedDate) {
          tempIniDateObject = moment(tempIniDateObject)
            .add(1, 'months')
            .toDate();
          currentIndexValues++;
        }
      }
    }

    let tempIniDateObject = moment(dateObject)
      .subtract(14, 'months')
      .startOf('month')
      .toDate();

    // Completar meses vacíos
    if (finded.length > 0) {
      let vacios = generateMonthlyDates(
        moment(finded[0].x).toDate(),
        moment(finded[finded.length - 1].x).toDate(),
        finded
      );

      vacios.sort((a, b) => a.x - b.x);
      finded = vacios;
    }

    finded.forEach((val) => {
      // Siempre el 1 de cada mes
      let findedDate = moment(val.x).startOf('month').toDate();

      if (findedDate < tempIniDateObject) {
        return;
      }

      el.values[currentIndexValues] = {
        ...el.values[currentIndexValues],
        monthKey: getErniValueMonthKey(val),
        value: val.y,
        y: val.y,
        color: getColour(index),
        name: el.name != null ? el.name : el.alias,
      };

      currentIndexValues++;
    });
  });

  // Para SNP (captura 1 / pro) la línea "Total" debe usar el indicador 10490 que viene de la API,
  // NO recalcularse a partir de buffers. Marcamos el 10490 original como serie de línea (demanda)
  // y dejamos 10488/10489 como barras.
  // Contar cuántos valores reales (no null) hay en los indicadores
  const countRealValues = (indicators) => {
    let count = 0;
    indicators.forEach((indicator) => {
      if (Array.isArray(indicator?.values)) {
        indicator.values.forEach((val) => {
          if (val?.value != null || val?.y != null) {
            count++;
          }
        });
      }
    });
    return count;
  };

  const hasRealValues = countRealValues(preData.indicators) > 3;

  if (hasRealValues) {
    const totalIndicator = preData.indicators.find((i) => i.id === 10490);
    if (totalIndicator) {
      totalIndicator.demanda = true;
      totalIndicator.legendtype = 'line';
      totalIndicator.color = ERNI_SNP_LINE_COLOUR;
      totalIndicator.name = ernitotal;
    }
  } else {
    preData.indicators.push({
      id: 10490,
      name: ernitotal,
      shortName: '% Energía RRTT Total',
      color: '#9C9C9C',
      legendtype: 'line',
      demanda: true,
      values: fillSimpleVals(valh),
      print_values: fillPrintVals(valh, date),
    });

    preData.indicators.map((elem, index) => {
      if (index === 2) {
        preData.indicators[3].values = preData.indicators[2]?.values.map(
          (el, ind) => {
            if (
              preData.indicators[3]?.values[ind] != null &&
              preData.indicators[3]?.values[ind].y != null
            ) {
              return {
                ...el,
                y: el.y + preData.indicators[3]?.values[ind].y,
                value: el.y + preData.indicators[3]?.values[ind].y,
              };
            } else {
              return {
                ...el,
                y: el.y + 0,
                value: el.y + 0,
              };
            }
          }
        );
        preData.indicators[3].print_values =
          preData.indicators[2]?.print_values.map((el, index) => {
            if (preData.indicators[3]?.values[index] != null) {
              return {
                ...el,
                y: preData.indicators[3]?.values[index].y,
                value: preData.indicators[3]?.values[index].value,
              };
            } else {
              return {
                ...el,
                y: 0,
                value: 0,
              };
            }
          });
        // Eliminar el elemento en la posición 2 de preData.indicators
        preData.indicators.splice(2, 1);
      }
    });
  }

  const dataLength =
    preData.indicators.length != 0
      ? preData.indicators[0].print_values.length
      : preData.indicators.length;
  const rectNoData5 = document.getElementById(noDataGraphic);
  const widget5 = document.getElementById(graphic);

  if (dataLength != 0) {
    if (widget5 != null) {
      widget5.classList.remove('no-data-graphic');
    }
    if (rectNoData5 != null) {
      rectNoData5.classList.add('hidden');
    }

    if (snp) {
      const rendered = renderErniSnpBarsLineGraph(preData.indicators, opts);
      if (!rendered) {
        if (widget5 != null) {
          widget5.classList.add('no-data-graphic');
        }
        if (rectNoData5 != null) {
          rectNoData5.classList.remove('hidden');
        }
      }
    } else {
      renderBalanceErniGraph(
        preData.indicators,
        i18next.t('month'),
        opts,
        snp,
        datason || [] // <-- evita .forEach sobre undefined
      );
    }
  } else {
    widget5?.classList.add('no-data-graphic');
    rectNoData5?.classList.remove('hidden');
    document
      .querySelector(graphicalmuadilla + ' > svg')
      ?.classList.add('hidden');
  }
};

let rendernoPeninsulares = (() => {
  return {
    renderErniWidget: (
      data,
      date,
      graphic,
      noDataGraphic,
      graphicalmuadilla,
      snp,
      datason,
      chartEndMoment
    ) =>
      renderErniWidget(
        data,
        date,
        graphic,
        noDataGraphic,
        graphicalmuadilla,
        snp,
        datason,
        chartEndMoment
      ),
  };
})();

export { rendernoPeninsulares };
